/**
 * One-time guided tour the first time a user opens /network. Five overlay
 * cards introducing Pitch → Stones → Sparks → Mindset → Events with the
 * Resmo mascot as the narrator. Persisted via localStorage so it never
 * runs twice.
 *
 * Triggered automatically on mount when localStorage flag isn't set. Users
 * can also re-open via the "Take the tour" pill in the page header
 * (parent passes `open` + `onClose`).
 */
import React, { useEffect, useState } from "react";
import { X, ArrowRight, ArrowLeft, CheckCircle2, Mic2, Users, Sparkles, Brain, MapPin } from "lucide-react";
import ResmoMascot from "../ResmoMascot";

const TOUR_KEY = "resmo:network-tour-done-v1";

const STEPS = [
  {
    id: "intro",
    accent: "#FF6A00",
    mascot: "satisfied",
    eyebrow: "Welcome to your Network",
    title: "I'll walk you through.",
    body: (
      <>
        Networking is half the job hunt — and the half no resume tool helps with.
        I'm your coach AND your cheerleader for the next 60 seconds. Take the tour
        and I'll show you what each tab does. (You can also skip — I won't be hurt.)
      </>
    ),
  },
  {
    id: "pitch",
    accent: "#FF6A00",
    mascot: "thinking",
    icon: <Mic2 size={20} />,
    eyebrow: "Tab 1 · Elevator Pitch",
    title: "Land in any room.",
    body: (
      <>
        Tell me a target role; I'll write you <em>three</em> 60-second pitches in
        different tones — warm, executive, and high-energy founder — plus
        rehearsal cues. Don't know your superpower? Ask me — I'll read your
        resume and tell you what makes you unforgettable.
      </>
    ),
  },
  {
    id: "stones",
    accent: "#10B981",
    mascot: "eating",
    icon: <Users size={20} />,
    eyebrow: "Tab 2 · Stepping Stones",
    title: "People pave your path.",
    body: (
      <>
        Add contacts manually or paste your LinkedIn Connections.csv (no
        scraping, your data stays yours). I score each person 0–100 on career
        fit, tell you why they matter, and stamp their stones green/amber/red
        based on how recently you've spoken. The <strong>Career Path</strong>
        view turns it all into a visual track.
      </>
    ),
  },
  {
    id: "sparks",
    accent: "#FFC145",
    mascot: "thinking",
    icon: <Sparkles size={20} />,
    eyebrow: "Tab 3 · Conversation Sparks",
    title: "Never blank on what to say.",
    body: (
      <>
        Pick any contact and I'll generate 5 openers, 5 deeper questions, and
        5 ways you can offer value first. Reciprocity wins — I'll make sure
        you walk in with something to <em>give</em>, not just ask for.
      </>
    ),
  },
  {
    id: "mindset",
    accent: "#0F1B2D",
    mascot: "satisfied",
    icon: <Brain size={20} />,
    eyebrow: "Tab 4 · Mindset Coach",
    title: "Walk in grounded.",
    body: (
      <>
        Tell me what you're about to walk into — a 40-person mixer, a cold DM,
        a coffee with a VP — and I'll give you a reframe, word swaps, a
        2-minute drill, and a single courage line to repeat at the door.
        I'm your cheerleader for the hard moments.
      </>
    ),
  },
  {
    id: "events",
    accent: "#FF6A00",
    mascot: "satisfied",
    icon: <MapPin size={20} />,
    eyebrow: "Tab 5 · Communities & Events",
    title: "Show up where it matters.",
    body: (
      <>
        I'll name real communities in your space (Slack, Discord, in-person
        chapters) and hand you copy-paste search queries for Eventbrite,
        Meetup, and Luma — plus one specific move to make in the next 7 days.
        That's the start.
      </>
    ),
  },
  {
    id: "done",
    accent: "#10B981",
    mascot: "satisfied",
    eyebrow: "You're set",
    title: "Let's pave your path.",
    body: (
      <>
        That's it — five tabs, one companion. Start anywhere. I'll be here
        every step. Whenever you want a refresher, click <strong>"Take the
        tour"</strong> at the top of the page.
      </>
    ),
  },
];

export default function NetworkTour({ open, onClose }) {
  const [shouldShow, setShouldShow] = useState(open);
  const [step, setStep] = useState(0);

  // Auto-run the first time
  useEffect(() => {
    if (open) {
      setShouldShow(true);
      setStep(0);
      return;
    }
    if (typeof window !== "undefined" && !localStorage.getItem(TOUR_KEY)) {
      setShouldShow(true);
      setStep(0);
    }
  }, [open]);

  const finish = () => {
    if (typeof window !== "undefined") localStorage.setItem(TOUR_KEY, "1");
    setShouldShow(false);
    setStep(0);
    onClose?.();
  };

  if (!shouldShow) return null;
  const s = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,27,45,0.62)", backdropFilter: "blur(4px)" }}
      data-testid="network-tour-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="rm-card-static w-full max-w-2xl p-5 md:p-7 relative"
        style={{ background: "#FBF7EE", borderColor: "#0F1B2D", boxShadow: "6px 6px 0 0 #FFC145" }}
      >
        <button
          onClick={finish}
          className="absolute top-2 right-2 p-1.5"
          style={{ color: "#0F1B2D" }}
          aria-label="Skip tour"
          data-testid="network-tour-skip"
        >
          <X size={18} />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-center">
          {/* Mascot */}
          <div className="md:col-span-3 flex justify-center">
            <ResmoMascot state={s.mascot} size={120} />
          </div>

          {/* Step content */}
          <div className="md:col-span-9">
            <div className="flex items-center gap-2 mb-2">
              {s.icon && <span style={{ color: s.accent }}>{s.icon}</span>}
              <div
                className="font-mono-rm text-[10px] uppercase tracking-widest"
                style={{ color: s.accent, fontWeight: 700 }}
              >
                · {s.eyebrow}
              </div>
            </div>
            <h3
              className="font-display uppercase leading-tight mb-3"
              style={{ fontSize: "clamp(1.25rem, 2.4vw, 1.7rem)", color: "#0F1B2D", letterSpacing: "-0.02em" }}
              data-testid={`network-tour-step-${s.id}-title`}
            >
              {s.title}
            </h3>
            <p className="text-sm md:text-base leading-relaxed mb-5" style={{ color: "#334155" }}>
              {s.body}
            </p>

            {/* Progress dots */}
            <div className="flex items-center gap-1.5 mb-4" data-testid="network-tour-progress">
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  className="rounded-full"
                  style={{
                    width: i === step ? 18 : 7,
                    height: 7,
                    background: i === step ? "#FF6A00" : i < step ? "#FFC145" : "#CBD5E1",
                    transition: "all 200ms ease",
                  }}
                />
              ))}
            </div>

            {/* Footer controls */}
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <button
                onClick={() => setStep((n) => Math.max(0, n - 1))}
                disabled={step === 0}
                className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                data-testid="network-tour-prev"
              >
                <ArrowLeft size={12} /> Back
              </button>
              <div className="flex gap-2">
                <button
                  onClick={finish}
                  className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                  data-testid="network-tour-finish"
                  title="You can always re-open the tour from the page header."
                >
                  Skip
                </button>
                {isLast ? (
                  <button
                    onClick={finish}
                    className="rm-btn rm-btn-primary text-xs px-3 py-2"
                    data-testid="network-tour-done"
                    style={{ boxShadow: "3px 3px 0 0 #0F1B2D" }}
                  >
                    <CheckCircle2 size={13} /> Start exploring
                  </button>
                ) : (
                  <button
                    onClick={() => setStep((n) => Math.min(STEPS.length - 1, n + 1))}
                    className="rm-btn rm-btn-primary text-xs px-3 py-2"
                    data-testid="network-tour-next"
                    style={{ boxShadow: "3px 3px 0 0 #0F1B2D" }}
                  >
                    Next <ArrowRight size={12} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
